// updated NAS Dashboard
import React, { useEffect, useState, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import DashboardLayout from '../components/Nav';
import { Combobox } from '@headlessui/react';
import getClientConfig from '../config';


import {
BarChart,
Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { ArrowTrendingUpIcon, ServerStackIcon, ComputerDesktopIcon, ExclamationCircleIcon, CpuChipIcon } from '@heroicons/react/24/outline';

export default function NASDashboard() {
  const [nasList, setNasList] = useState([]);
  const [selectedNAS, setSelectedNAS] = useState('');
  const [logsData, setLogsData] = useState([]);
  const [range, setRange] = useState('week');
  const [uptime, setUptime] = useState('');
  const [diskUsage, setDiskUsage] = useState({ total_bytes: 0, used_bytes: 0, free_bytes: 0 });
  const [activityLogs, setActivityLogs] = useState([]);
  const [licenseStatus, setLicenseStatus] = useState({
    valid: false,
    used: 0,
    allowed: 0,
  });
  const [cpuUsage, setCpuUsage] = useState(0);
  const token = localStorage.getItem('auth_token');

// 🟢 Disk usage
const diskUtilizationPercent = Math.round((diskUsage.used_bytes / (diskUsage.total_bytes || 1)) * 100);
const diskUsageColor = diskUtilizationPercent >= 70 ? '#dc2626' : diskUtilizationPercent >= 50 ? '#f59e0b' : '#10b981';

const diskPieData = [
  { name: 'Used', value: diskUsage.used_bytes },
  { name: 'Free', value: diskUsage.free_bytes },
];

const [memoryUsage, setMemoryUsage] = useState({ used_bytes: 0, free_bytes: 0 });
const [query, setQuery] = useState('');
const [nasDropdownOpen, setNasDropdownOpen] = useState(false);
const { API_BASE_URL } = getClientConfig();
useEffect(() => {
  document.title = "Dashboard | IP Intelligence Portal";
}, []);

const filteredNAS = useMemo(() => {
  if (!query) return nasList;
  return nasList.filter((nas) =>
    `${nas.name} ${nas.ip_address}`.toLowerCase().includes(query.toLowerCase())
  );
}, [query, nasList]);

const handleNASChange = (ip) => {
  if (ip) {
    setSelectedNAS(ip);
    // Directly fetch logs for the selected NAS
    fetchLogs(ip, range); 
  }
};


// 🟢 Memory usage (placeholder if you’re adding later)


useEffect(() => {
    fetchNASList();
    fetchUptime();
    fetchDisk();
    fetchMemoryUsage();
    fetchLicenseStatus();
    fetchCPUUsage();
    fetchActivityLogs();
  }, []);
  
  useEffect(() => {
    if (selectedNAS && range) {
      fetchLogs(selectedNAS, range);
    }
  }, [selectedNAS, range]);
  

  useEffect(() => {
    if (selectedNAS && range) {
      fetchLogs();
    }
  }, [selectedNAS, range]);


  const fetchMemoryUsage = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/memory-usage`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setMemoryUsage(data);
    } catch {
      console.error('Failed to fetch memory usage');
    }
  };

  const fetchNASList = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/nas-list`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setNasList(data);
      if (data.length > 0) setSelectedNAS(data[0].ip_address);
    } catch (err) {
      console.error('Failed to load NAS list');
    }
  };

  const fetchLogs = async (nasIp = selectedNAS, r = range) => {
    try {
      const res = await fetch(`${API_BASE_URL}/nas-log-history?ip=${nasIp}&range=${r}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
  
      // Check if response is null, empty, or not an array
      if (!data || !Array.isArray(data) || data.length === 0) {
        setLogsData([]); // clear the graph
      } else {
        setLogsData(data.reverse());
      }
    } catch {
      console.error('Failed to fetch NAS log history');
      setLogsData([]); // clear on error as well
    }
  };
  
  

  const fetchUptime = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/system-uptime`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUptime(data.uptime_human);
    } catch {
      console.error('Failed to fetch system uptime');
    }
  };

  const fetchDisk = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/disk-usage`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setDiskUsage(data);
    } catch {
      console.error('Failed to fetch disk usage');
    }
  };

  const fetchCPUUsage = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/cpu-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCpuUsage(data.percent.toFixed(1)); // Round to 1 decimal place
    } catch (err) {
      console.error('Failed to fetch CPU usage');
    }
  };

  const fetchLicenseStatus = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/license-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setLicenseStatus({
        valid: data.valid,
        used: data.used,
        allowed: data.allowed,
      });
    } catch (err) {
      console.error('Failed to fetch license status');
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/dash-activity-fetch?limit=20&page=1`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setActivityLogs(data.logs || []);
    } catch {
      console.error('Failed to fetch activity logs');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const memoryUtilizationPercent = Math.round(
    (memoryUsage.used_bytes / ((memoryUsage.used_bytes + memoryUsage.free_bytes) || 1)) * 100
  );
  const memoryUsageColor =
    memoryUtilizationPercent >= 70
      ? '#dc2626'
      : memoryUtilizationPercent >= 50
      ? '#f59e0b'
      : '#10b981';
  
  const memoryPieData = [
    { name: 'Used', value: memoryUsage.used_bytes },
    { name: 'Free', value: memoryUsage.free_bytes },
  ];

  const utilizationPercent = Math.round((diskUsage.used_bytes / (diskUsage.total_bytes || 1)) * 100);
  const usageColor = utilizationPercent >= 70 ? '#dc2626' : utilizationPercent >= 50 ? '#f59e0b' : '#10b981';

  const pieData = [
    { name: 'Used', value: diskUsage.used_bytes },
    { name: 'Free', value: diskUsage.free_bytes },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-8 animate-fade-in">
        {utilizationPercent >= 70 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            <ExclamationCircleIcon className="w-5 h-5" />
            <p className="text-sm font-medium">
              Warning: Disk usage is at {utilizationPercent}% capacity. Please consider freeing up space or adding more storage.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-2xl p-4">
            <h5 className="text-base font-semibold text-gray-700 mb-1 flex items-center gap-2">
              <ServerStackIcon className="w-5 h-5 text-blue-600" /> System Uptime
            </h5>
            <p className="text-xl font-mono text-blue-900 mt-1">{uptime || 'Loading...'}</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-100 to-white border rounded-xl shadow">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">License Status</h3>
              <p className="text-sm text-gray-600">{licenseStatus.used} of {licenseStatus.allowed} NAS used</p>
            </div>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${licenseStatus.used <= licenseStatus.allowed ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
  {licenseStatus.used <= licenseStatus.allowed ? 'Valid' : 'Invalid'}
</span>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-100 rounded-2xl p-4">
            <h5 className="text-base font-semibold text-gray-700 mb-1 flex items-center gap-2">
              <CpuChipIcon className="w-5 h-5 text-purple-600" /> CPU Utilization
            </h5>
            <p className="text-xl font-mono text-purple-900 mt-1">{cpuUsage}%</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 shadow-xl rounded-2xl p-6 hover:shadow-2xl transition">
          <div className="flex justify-between items-center mb-4">
            <h5 className="text-lg font-semibold text-blue-700 flex items-center gap-2">
              <ArrowTrendingUpIcon className="w-5 h-5" /> Log Ingestion Trend
            </h5>
            <div className="flex items-center gap-4">


            <div className="w-64 relative">
  <Combobox value={selectedNAS} onChange={handleNASChange}>
    <div className="relative">
      <Combobox.Input
        className="w-full border border-gray-300 rounded-md px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Search NAS..."
        onChange={(e) => setQuery(e.target.value)}
        displayValue={(ip) => {
          const found = nasList.find((n) => n.ip_address === ip);
          return found ? `${found.name} (${found.ip_address})` : '';
        }}
        onClick={() => setNasDropdownOpen((prev) => !prev)}
      />
      <button
        type="button"
        onClick={() => setNasDropdownOpen((prev) => !prev)}
        className="absolute inset-y-0 right-0 flex items-center pr-3"
        tabIndex={-1}
      >
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 transform transition-transform ${
            nasDropdownOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <Combobox.Options
        static
        className={`absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-300 bg-white shadow-lg text-sm ${
          nasDropdownOpen ? '' : 'hidden'
        }`}
      >
        {filteredNAS.length === 0 ? (
          <div className="px-4 py-2 text-gray-500">No matches found.</div>
        ) : (
          filteredNAS.map((nas) => (
            <Combobox.Option
              key={nas.ip_address}
              value={nas.ip_address}
              className={({ active }) =>
                `px-4 py-2 cursor-pointer ${
                  active ? 'bg-blue-600 text-white' : 'text-gray-800'
                }`
              }
            >
              {nas.name} ({nas.ip_address})
            </Combobox.Option>
          ))
        )}
      </Combobox.Options>
    </div>
  </Combobox>
</div>





              <div className="flex gap-2">
                {['week', 'month', 'year'].map((r) => (
                  <button
                    key={r}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                      range === r ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                    }`}
                    onClick={() => setRange(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ width: '100%', height: 300 }}>
  {logsData.length === 0 ? (
    <div className="text-center text-gray-500 pt-24">No logs available for the selected NAS and range.</div>
  ) : (
    <ResponsiveContainer>
      <BarChart
        data={logsData}
        margin={{ left: 0, right: 20, top: 20, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tickFormatter={(tick) => {
            const date = new Date(tick);
            return date.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
          }}
        />
        <YAxis allowDecimals={false} />
        <Tooltip
          contentStyle={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
          labelFormatter={(label) => {
            const date = new Date(label);
            return date.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
          }}
          formatter={(value) => [`${value}`, 'Log Count']}
        />
        <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} barSize={20} />
      </BarChart>
    </ResponsiveContainer>
  )}
</div>

        </div>

        


        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  {/* Disk Usage */}
  <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-2xl p-6">
    <h5 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
      <ComputerDesktopIcon className="w-5 h-5 text-indigo-600" /> Disk Usage — {utilizationPercent}% used
    </h5>
    <div className="w-full h-64">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
            label={({ value }) => formatBytes(value)}
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={index === 0 ? usageColor : '#4f46e5'} />
            ))}
          </Pie>
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>

  {/* Memory Usage */}
  <div className="bg-gradient-to-br from-pink-50 to-white border border-pink-100 rounded-2xl p-6">
    <h5 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
      <ComputerDesktopIcon className="w-5 h-5 text-pink-600" /> Memory Usage — {memoryUtilizationPercent}% used
    </h5>
    <div className="w-full h-64">
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={memoryPieData}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
            label={({ value }) => formatBytes(value)}
          >
            {memoryPieData.map((entry, index) => (
              <Cell key={`cell-mem-${index}`} fill={index === 0 ? memoryUsageColor : '#4f46e5'} />
            ))}
          </Pie>
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
</div>


        <div className="bg-white shadow border rounded-xl p-6">
          <h5 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity Logs</h5>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left text-gray-600">
              <thead>
                <tr>
                  <th className="px-4 py-2 border-b">Time</th>
                  <th className="px-4 py-2 border-b">User</th>
                  <th className="px-4 py-2 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((log, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-2 border-b">{log.user_email}</td>
                    <td className="px-4 py-2 border-b">{log.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
